// Resolve the unmapped asset class from dropdown options by label, not by enum value.
// OrionAssetClassMapping labels come from assetClassDescriptor.GetDescription(AssetClass.Unknown),
// which is "Unknown" for the default descriptor; onboarding also appends { label: "Unknown", value: 18 }.
// Do not use "Other" here — that label is Ally-tenant-specific for the same value but this page
// only partitions rows when the option label is "Unknown".
const UNKNOWN_ASSET_CLASS_LABEL = 'Unknown';

export interface AssetClassOption {
  label: string;
  value: number;
}

export interface AssetClassMapping {
  ExternalAssetClassName: string;
  InternalAssetClass: number;
}

export interface AssetClassMappingWithIndex extends AssetClassMapping {
  originalIndex: number;
  currentAssetClass?: AssetClassOption;
}

export interface AssetClassTableConfig {
  title: string;
  headerTooltip: string;
}

export interface PartitionedMappings {
  unknownMappings: AssetClassMappingWithIndex[];
  otherMappings: AssetClassMappingWithIndex[];
}

export const UNKNOWN_TABLE: AssetClassTableConfig = {
  title: 'Map the following Unknowns',
  headerTooltip:
    'Selecting the appropriate Orion Planning asset class will ensure the asset allocation and returns are properly setup for financial planning calculations and CMAs.',
};

export const MAPPED_TABLE: AssetClassTableConfig = {
  title: 'Mapped Asset Classes',
  headerTooltip: 'Search to lookup Orion asset classes and make changes to mappings. ',
};

export const getUnknownAssetClassValue = (
  assetClassesOptions: AssetClassOption[],
): number | undefined =>
  assetClassesOptions.find(({ label }) => label === UNKNOWN_ASSET_CLASS_LABEL)?.value;

export const partitionMappings = (
  assetClassMappings: AssetClassMapping[],
  unknownAssetClassValue: number | undefined,
  assetClassesOptions: AssetClassOption[],
): PartitionedMappings =>
  assetClassMappings.reduce<PartitionedMappings>(
    (partitions, mapping, originalIndex) => {
      const mappingWithIndex: AssetClassMappingWithIndex = {
        ...mapping,
        originalIndex,
        currentAssetClass: assetClassesOptions.find(
          ({ value }) => mapping.InternalAssetClass === value,
        ),
      };

      if (mapping.InternalAssetClass === unknownAssetClassValue) {
        partitions.unknownMappings.push(mappingWithIndex);
      } else {
        partitions.otherMappings.push(mappingWithIndex);
      }

      return partitions;
    },
    { unknownMappings: [], otherMappings: [] },
  );

export const filterMappingsBySearch = (
  mappings: AssetClassMappingWithIndex[],
  searchQuery: string,
): AssetClassMappingWithIndex[] => {
  const trimmedSearch = searchQuery.trim().toLowerCase();

  if (!trimmedSearch) {
    return mappings;
  }

  return mappings.filter(mapping => {
    const externalNameMatches = mapping.ExternalAssetClassName.toLowerCase().includes(
      trimmedSearch,
    );
    const currentAssetClassMatches =
      mapping.currentAssetClass?.label?.toLowerCase().includes(trimmedSearch) ?? false;

    return externalNameMatches || currentAssetClassMatches;
  });
};
